/**
 * The six stages an order passes through, and where a given status puts it.
 *
 * This is the data behind the stage strip at the top of the order timeline.
 * A status puts the order IN a stage: the stages before it are done, that
 * stage is in process, the rest are waiting.
 *
 * AB#22096 — it used to count "stages finished" one too high (lifted from the
 * old stepper): an order In Production painted In Production as Complete and
 * Order Shipped as "In process…", and customers read that as shipped. Every
 * status from factory_approved on was one stage ahead, and quality_control
 * was not mapped at all (it fell to Order Created).
 */

export type StageKey = 'created' | 'artwork' | 'sample' | 'production' | 'shipped' | 'completed';

export type StageState = 'done' | 'current' | 'waiting';

export interface OrderStage {
  key: StageKey;
  label: string;
  state: StageState;
  /** The line under the label: Complete / In process… / Waiting… */
  detail: string;
}

export const STAGES: ReadonlyArray<{ key: StageKey; label: string }> = [
  { key: 'created', label: 'Order Created' },
  { key: 'artwork', label: 'Artwork Approval' },
  { key: 'sample', label: 'Sample Design' },
  { key: 'production', label: 'In Production' },
  { key: 'shipped', label: 'Order Shipped' },
  { key: 'completed', label: 'Order Completed' },
];

const DETAIL: Record<StageState, string> = {
  done: 'Complete',
  current: 'In process…',
  waiting: 'Waiting…',
};

/** The index of the stage a status is in (0 = Order Created … 5 = Order Completed). */
export const stageOf = (status: string): number => {
  switch ((status || '').toLowerCase()) {
    case 'factory_approved':
      return 1; // Artwork Approval
    case 'submitted_for_customer_review':
    case 'customer_approved':
    case 'customer_rejected':
      return 2; // Sample Design
    case 'production_files_uploaded':
    case 'in_production':
    case 'quality_control':
    case 'waiting_for_consolidation': // AB#22510 — made, not yet shipped
      return 3; // In Production
    case 'shipped':
      return 4; // Order Shipped
    case 'completed':
      return 5; // Order Completed
    default:
      // submitted_for_factory_review, order_approve, order_cancel, the quote
      // statuses, pending_for_factory_assignment, factory_rejected, on_hold,
      // and anything unknown: Order Created.
      return 0;
  }
};

/** How many stages are behind the order: the stages before the one it is in; a completed order has all six. */
export const stagesDone = (status: string): number =>
  (status || '').toLowerCase() === 'completed' ? STAGES.length : stageOf(status);

export const orderStages = (status: string): OrderStage[] => {
  const done = stagesDone(status);

  return STAGES.map((stage, index) => {
    const state: StageState = index < done ? 'done' : index === done ? 'current' : 'waiting';
    return { ...stage, state, detail: DETAIL[state] };
  });
};
